import { memo, useCallback, useState } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import { HoverCard } from '@air/primitive-hover-card';

import { PublicShowcaseNavBoardMenuItem } from '~/components/v2/components/showcase/PublicShowcaseNavBoardMenuItem';
import { PublicShowcaseNavLink } from '~/components/v2/components/showcase/PublicShowcaseNavLink';
import { type PublicShowcaseNavEntry } from '~/components/v2/components/showcase/publicShowcaseNavEntries';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

export type PublicShowcaseNavPageEntryProps = {
  entry: PublicShowcaseNavEntry;
};

/**
 * One page destination in the desktop sticky nav bar (Home or a collection sub-page). Fetches the
 * page's own boards; with ≥1 the ENTIRE entry — label plus a chevron affordance — becomes the hover
 * card's trigger: hovering (or keyboard-focusing) anywhere on it opens a dropdown of the page's
 * boards, each routing to the board (carrying the page's `?page={slug}` trail), while clicking the
 * label still navigates to the page. This mirrors the prototype's whole-entry hover target. A page
 * with no boards shows no chevron and never opens a dropdown — visually the plain link. External
 * links never mount this component (see `PublicShowcaseNav`), so the query only runs for real pages.
 *
 * Open is CONTROLLED and gated on `hasBoards`, for two reasons: (1) the HoverCard root is rendered
 * unconditionally so the link is never re-parented when the async board query resolves — it can't
 * remount under a viewer mid-click; and (2) a board-less entry forces open shut, so hovering it never
 * flashes an empty popover. The chevron and board content only render once boards are known.
 */
export const PublicShowcaseNavPageEntry = memo(({ entry }: PublicShowcaseNavPageEntryProps) => {
  const { useNavPageBoards } = usePublicShowcaseContext();
  const { boardCount, boards } = useNavPageBoards({ pageCollectionId: entry.pageId });
  const [isOpen, setIsOpen] = useState(false);

  // `total` is the authoritative count off the response.
  const hasBoards = boardCount > 0;

  // Always controlled (open is always a boolean, so the root never flips controlled↔uncontrolled).
  // A board-less entry can never open, so hovering it shows nothing.
  const handleOpenChange = useCallback((open: boolean) => setIsOpen(open), []);

  return (
    <HoverCard
      align="start"
      className="min-w-[12rem]"
      closeDelay={200}
      onOpenChange={handleOpenChange}
      open={hasBoards && isOpen}
      openDelay={0}
      trigger={
        // The whole entry is the hover target: hovering the label or the chevron opens the dropdown,
        // and focus bubbling from the label means tabbing to it opens the dropdown too. The label
        // stays an independent link — clicking it navigates to the page.
        <div className="inline-flex items-end" data-testid="PUBLIC_COLLECTION_NAV_ENTRY">
          <PublicShowcaseNavLink entry={entry} />
          {hasBoards ? (
            <span
              aria-hidden
              className="inline-flex h-11 shrink-0 items-center px-1 text-gray-10"
              data-testid="PUBLIC_COLLECTION_NAV_BOARD_CHEVRON"
            >
              <ChevronDownIcon size="sm" />
            </span>
          ) : null}
        </div>
      }
    >
      {/* Portaled out of the outer nav by the hover card, so a nested navigation landmark is valid
          and gives the board links a screen-reader-addressable group name. */}
      <nav
        aria-label={`${entry.label} boards`}
        className="flex flex-col"
        data-testid="PUBLIC_COLLECTION_NAV_BOARD_MENU"
      >
        {boards.map((board) => (
          <PublicShowcaseNavBoardMenuItem board={board} key={board.id} pageId={entry.pageId} slug={entry.slug} />
        ))}
      </nav>
    </HoverCard>
  );
});

PublicShowcaseNavPageEntry.displayName = 'PublicShowcaseNavPageEntry';
